import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  Modal,
  Paper,
  Button,
  Divider,
  IconButton,
  Fade,
  Zoom,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  KeyboardArrowUp,
  ArrowForward,
  LocationOn,
  Email,
  AccessTime,
  VerifiedUser,
  Security,
  AccountBalance,
  Close,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";

const FooterWrapper = styled(Box)({
  backgroundColor: "#f8fafc",
  color: "#334155",
  position: "relative",
  overflow: "hidden",
  borderTop: "1px solid rgba(147, 96, 151, 0.15)",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: "linear-gradient(90deg, #936097 0%, #77c8c7 50%, #936097 100%)",
  },
});

const LogoContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  marginBottom: "16px",
});

const FooterLink = styled(Link)({
  display: "flex",
  alignItems: "center",
  color: "#64748b",
  textDecoration: "none",
  marginBottom: "12px",
  fontSize: "0.92rem",
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  transition: "all 0.25s ease",
  cursor: "pointer",
  "&:hover": {
    color: "#936097",
    transform: "translateX(6px)",
  },
});

const SectionTitle = styled(Typography)({
  position: "relative",
  marginBottom: "20px",
  fontWeight: 700,
  fontSize: "1.15rem",
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  color: "#1e293b",
  letterSpacing: "0.3px",
  "&::after": {
    content: '""',
    position: "absolute",
    left: 0,
    bottom: "-6px",
    height: "2.5px",
    width: "36px",
    background: "linear-gradient(90deg, #936097, #77c8c7)",
    borderRadius: "2px",
  },
});

const ScrollTopButton = styled(IconButton)({
  position: "fixed",
  right: "24px",
  bottom: "24px",
  background: "linear-gradient(135deg, #936097 0%, #77c8c7 100%)",
  color: "#fff",
  boxShadow: "0 6px 20px rgba(147, 96, 151, 0.35)",
  zIndex: 99,
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-4px) scale(1.05)",
    boxShadow: "0 10px 25px rgba(147, 96, 151, 0.5)",
  },
});

const ModalPaper = styled(Paper)({
  backgroundColor: "#ffffff",
  color: "#1e293b",
  borderRadius: "20px",
  boxShadow: "0 25px 60px rgba(0, 0, 0, 0.15)",
  border: "1px solid rgba(147, 96, 151, 0.18)",
  maxHeight: "85vh",
  overflowY: "auto",
});

const Footer = () => {
  const [modalType, setModalType] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleOpen = (type) => setModalType(type);
  const handleClose = () => setModalType(null);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <FooterWrapper>
      <Container maxWidth="xl" sx={{ pt: { xs: 6, md: 8 }, pb: 4 }}>
        <Grid container spacing={{ xs: 4, md: 5 }}>
          {/* Column 1: Company Info */}
          <Grid item xs={12} md={4}>
            <LogoContainer>
              <img
                src="/mainlogo.png"
                alt="Velavan Finance"
                style={{
                  height: "54px",
                  width: "auto",
                  objectFit: "contain",
                  filter: "drop-shadow(0 2px 8px rgba(119, 200, 199, 0.35))",
                }}
              />
              <Box>
                <Typography
                  variant="h6"
                  component="a"
                  href="/"
                  sx={{
                    fontFamily: "'Playfair Display', serif",
                    fontWeight: 700,
                    color: "#1e293b",
                    letterSpacing: "0.5px",
                    lineHeight: 1.1,
                    textDecoration: "none",
                    display: "block",
                  }}
                >
                  VELAVAN FINANCE
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    color: "#936097",
                    fontWeight: 700,
                    letterSpacing: "1.5px",
                    fontSize: "0.68rem",
                    display: "block",
                  }}
                >
                  FINANCE & LEASING SOLUTIONS
                </Typography>
              </Box>
            </LogoContainer>

            <Typography
              variant="body2"
              sx={{
                mb: 3,
                color: "#64748b",
                lineHeight: 1.75,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: "0.92rem",
              }}
            >
              Velavan Finance is your premier destination for secure, fast, and transparent financial solutions. Specializing in high-value Gold Loans, easy Vehicle Loans, and tailored financing with market-leading interest rates.
            </Typography>

            {/* Trust Badges */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <Chip
                icon={<VerifiedUser sx={{ fontSize: "16px !important", color: "#0d9488 !important" }} />}
                label="100% Transparent"
                size="small"
                sx={{
                  backgroundColor: "rgba(119, 200, 199, 0.15)",
                  color: "#0f766e",
                  border: "1px solid rgba(119, 200, 199, 0.35)",
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                }}
              />
              <Chip
                icon={<Security sx={{ fontSize: "16px !important", color: "#936097 !important" }} />}
                label="Safe Vault Storage"
                size="small"
                sx={{
                  backgroundColor: "rgba(147, 96, 151, 0.12)",
                  color: "#936097",
                  border: "1px solid rgba(147, 96, 151, 0.25)",
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                }}
              />
              <Chip
                icon={<AccountBalance sx={{ fontSize: "16px !important", color: "#0d9488 !important" }} />}
                label="Instant Disbursal"
                size="small"
                sx={{
                  backgroundColor: "rgba(119, 200, 199, 0.15)",
                  color: "#0f766e",
                  border: "1px solid rgba(119, 200, 199, 0.35)",
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                }}
              />
            </Box>
          </Grid>

          {/* Column 2: Loan Solutions */}
          <Grid item xs={12} sm={6} md={2.5}>
            <SectionTitle variant="h6">Loan Solutions</SectionTitle>
            <Box sx={{ mt: 2.5 }}>
              <FooterLink component={RouterLink} to="/gold-loan">
                <ArrowForward sx={{ fontSize: 13, mr: 1, color: "#936097" }} /> Gold Loan (Jewel)
              </FooterLink>
              <FooterLink component={RouterLink} to="/vehicle-loan">
                <ArrowForward sx={{ fontSize: 13, mr: 1, color: "#936097" }} /> Two & Four Wheeler
              </FooterLink>
              <FooterLink component={RouterLink} to="/loan-selection">
                <ArrowForward sx={{ fontSize: 13, mr: 1, color: "#936097" }} /> Loan Selection
              </FooterLink>
              <FooterLink component={RouterLink} to="/contact">
                <ArrowForward sx={{ fontSize: 13, mr: 1, color: "#936097" }} /> Chit & Savings Schemes
              </FooterLink>
              <FooterLink component={RouterLink} to="/emi">
                <ArrowForward sx={{ fontSize: 13, mr: 1, color: "#936097" }} /> Loan EMI Calculator
              </FooterLink>
            </Box>
          </Grid>

          {/* Column 3: Quick Links & Legal */}
          <Grid item xs={12} sm={6} md={2.5}>
            <SectionTitle variant="h6">Quick Links</SectionTitle>
            <Box sx={{ mt: 2.5 }}>
              <FooterLink onClick={() => handleOpen("about")}>
                <ArrowForward sx={{ fontSize: 13, mr: 1, color: "#77c8c7" }} /> About Velavan Finance
              </FooterLink>
              <FooterLink component={RouterLink} to="/contact">
                <ArrowForward sx={{ fontSize: 13, mr: 1, color: "#77c8c7" }} /> Contact Us
              </FooterLink>
              <FooterLink component={RouterLink} to="/emi">
                <ArrowForward sx={{ fontSize: 13, mr: 1, color: "#77c8c7" }} /> Financial Planner
              </FooterLink>
              <FooterLink onClick={() => handleOpen("privacy")}>
                <ArrowForward sx={{ fontSize: 13, mr: 1, color: "#77c8c7" }} /> Privacy & Security Policy
              </FooterLink>
              <FooterLink onClick={() => handleOpen("terms")}>
                <ArrowForward sx={{ fontSize: 13, mr: 1, color: "#77c8c7" }} /> Terms & Conditions
              </FooterLink>
            </Box>
          </Grid>

          {/* Column 4: Contact & Office Info */}
          <Grid item xs={12} md={3}>
            <SectionTitle variant="h6">Office & Support</SectionTitle>
            <Box sx={{ mt: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <LocationOn sx={{ color: "#936097", fontSize: 20, mt: 0.3 }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: "#64748b",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    lineHeight: 1.5,
                  }}
                >
                  Velavan Finance Pvt Ltd, Tamil Nadu, India
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Email sx={{ color: "#936097", fontSize: 18 }} />
                <Typography
                  variant="body2"
                  component="a"
                  href="mailto:poovarasan@velavanfinance.com"
                  sx={{
                    color: "#64748b",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    textDecoration: "none",
                    fontWeight: 500,
                    "&:hover": { color: "#936097" },
                  }}
                >
                  poovarasan@velavanfinance.com
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <AccessTime sx={{ color: "#0d9488", fontSize: 18 }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: "#64748b",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                  }}
                >
                  Mon - Sat: 9:00 AM - 6:00 PM
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, borderColor: "rgba(147, 96, 151, 0.12)" }} />

        {/* Bottom Bar */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            textAlign: { xs: "center", sm: "left" },
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#94a3b8",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: "0.85rem",
            }}
          >
            © {new Date().getFullYear()} VELAVAN FINANCE PVT LTD. All rights reserved.
          </Typography>

          <Box sx={{ display: "flex", gap: 2.5 }}>
            <Link
              onClick={() => handleOpen("privacy")}
              sx={{
                color: "#64748b",
                fontSize: "0.85rem",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                cursor: "pointer",
                textDecoration: "none",
                fontWeight: 500,
                "&:hover": { color: "#936097" },
              }}
            >
              Privacy Policy
            </Link>
            <Link
              onClick={() => handleOpen("terms")}
              sx={{
                color: "#64748b",
                fontSize: "0.85rem",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                cursor: "pointer",
                textDecoration: "none",
                fontWeight: 500,
                "&:hover": { color: "#936097" },
              }}
            >
              Terms of Service
            </Link>
          </Box>
        </Box>
      </Container>

      {/* Floating Scroll-to-Top Button */}
      <Zoom in={showScrollTop}>
        <ScrollTopButton onClick={scrollToTop} aria-label="scroll to top">
          <KeyboardArrowUp sx={{ fontSize: 28 }} />
        </ScrollTopButton>
      </Zoom>

      {/* Interactive Modals */}
      <Modal open={modalType !== null} onClose={handleClose} closeAfterTransition>
        <Fade in={modalType !== null}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "92%", sm: 550 },
              maxWidth: 600,
              outline: "none",
              p: 2,
            }}
          >
            <ModalPaper sx={{ p: { xs: 3, md: 4 } }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2.5,
                  pb: 1.5,
                  borderBottom: "1px solid rgba(147, 96, 151, 0.15)",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    fontFamily: "'Playfair Display', serif",
                    color: "#1e293b",
                  }}
                >
                  {modalType === "about" && "About Velavan Finance"}
                  {modalType === "privacy" && "Privacy & Data Protection"}
                  {modalType === "terms" && "Terms & Conditions"}
                </Typography>
                <IconButton onClick={handleClose} sx={{ color: "#64748b" }}>
                  <Close />
                </IconButton>
              </Box>

              {modalType === "about" && (
                <Box sx={{ color: "#475569", lineHeight: 1.75, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    <strong>Velavan Finance</strong> was founded with a deep commitment to providing fast, reliable, and respectful financial assistance to hardworking individuals, families, and growing businesses across Tamil Nadu.
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    With a customer-first philosophy, we ensure:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2.5, mb: 2 }}>
                    <li><strong>Market-Leading Gold Valuation:</strong> Maximum cash against your gold jewellery.</li>
                    <li><strong>Ultra-Low Interest Rates:</strong> Clear, honest interest calculations without hidden processing charges.</li>
                    <li><strong>Certified Safe Vaults:</strong> 24/7 security, multi-tier insurance, and tamper-evident sealing for all pledged assets.</li>
                    <li><strong>Same-Day Disbursal:</strong> Walk in with your collateral or documents and leave with your funds in under an hour.</li>
                  </Box>
                  <Typography variant="body2">
                    Our branches are staffed with certified loan specialists dedicated to helping you make the most informed and prudent financial decisions.
                  </Typography>
                </Box>
              )}

              {modalType === "privacy" && (
                <Box sx={{ color: "#475569", lineHeight: 1.75, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    At Velavan Finance, your privacy and financial security are our top priorities. We adhere to rigorous data privacy standards to protect your personal and financial information.
                  </Typography>
                  <Box component="ul" sx={{ pl: 2.5, mb: 2 }}>
                    <li><strong>Information Security:</strong> All customer records are stored with end-to-end encryption.</li>
                    <li><strong>Zero Data Selling:</strong> We will never sell, rent, or trade your contact information to third-party telemarketers.</li>
                    <li><strong>Regulatory Compliance:</strong> We collect only necessary KYC documents as mandated by Indian lending regulations.</li>
                  </Box>
                  <Typography variant="body2">
                    For any privacy-related inquiries or data access requests, please reach out to our grievance officer at <em>poovarasan@velavanfinance.com</em>.
                  </Typography>
                </Box>
              )}

              {modalType === "terms" && (
                <Box sx={{ color: "#475569", lineHeight: 1.75, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    By using the services of Velavan Finance Pvt Ltd, you agree to comply with our lending guidelines:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2.5, mb: 2 }}>
                    <li><strong>Eligibility:</strong> Applicants must be at least 18 years of age with valid government-issued ID proofs.</li>
                    <li><strong>Collateral Inspection:</strong> Gold jewellery and vehicles are evaluated accurately according to prevailing market standards.</li>
                    <li><strong>Repayment:</strong> Customers are expected to honor agreed monthly interest or EMI payments on time to prevent default penalties.</li>
                  </Box>
                  <Typography variant="body2">
                    Detailed terms and schedule of charges are provided in your physical loan sanction letter.
                  </Typography>
                </Box>
              )}

              <Box sx={{ mt: 3, textAlign: "right" }}>
                <Button
                  variant="contained"
                  onClick={handleClose}
                  sx={{
                    background: "linear-gradient(135deg, #936097 0%, #77c8c7 100%)",
                    color: "#fff",
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: "10px",
                    px: 3,
                  }}
                >
                  Understood
                </Button>
              </Box>
            </ModalPaper>
          </Box>
        </Fade>
      </Modal>
    </FooterWrapper>
  );
};

export default Footer;